import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { SUBMIT_USER_GIVEAWAY } from "queries/mutations";
import { GET_GIVEAWAYS_BY_ID, GET_OPEN_GIVEAWAYS } from "queries/queries";
import info from "../../assets/images/info.svg";
import backArrow from "../../assets/images/left_arrow.png";
import closeIcon from "../../assets/images/close_icon.png";
import useMakeScrollable from "custom_hooks/make-scrollable";

type RouteParams = { id: string };

const GiveawayDetail: React.FC = () => {
  const { id } = useParams<RouteParams>();
  const history = useHistory();

  const { loading, error, data } = useQuery<{ getGiveaway }>(
    GET_GIVEAWAYS_BY_ID,
    { variables: { id } }
  );

  const giveaway = data?.getGiveaway ?? null;

  const openGiveaway = useQuery(GET_OPEN_GIVEAWAYS, {
    fetchPolicy: "no-cache",
  });
  
  const openGiveaways = openGiveaway?.data?.getOpenGiveaways;

  const isOpenForUser = useMemo(() => {
    return !!(giveaway?.id && openGiveaways?.some(g => g.id === giveaway.id));
  }, [openGiveaways, giveaway?.id]);

  const headers = useMemo<string[]>(
    () =>
      (giveaway?.imageHeaders?.length ? giveaway.imageHeaders : ["Foto hochladen"]).filter(
        Boolean
      ) as string[],
    [giveaway?.imageHeaders]
  );

  const [files, setFiles] = useState<(File | null)[]>(() =>
    Array.from({ length: headers.length }, () => null)
  );
  useEffect(() => {
    setFiles((prev) => {
      const next = prev.slice(0, headers.length);
      while (next.length < headers.length) next.push(null);
      return next;
    });
  }, [headers.length]);

  useMakeScrollable()

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const setInputRef =
    (idx: number) =>
    (el: HTMLInputElement | null): void => {
      inputRefs.current[idx] = el;
    };

  const onPick =
    (idx: number) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const f = e.target.files?.[0] ?? null;
      setFiles((prev) => {
        const next = prev.slice();
        next[idx] = f;
        return next;
      });
    };


  const [showRules, setShowRules] = useState(false);
  useEffect(() => {
    if (!showRules) return;
    const onKey = (e: KeyboardEvent) =>
      e.key === "Escape" && setShowRules(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showRules]);

  const [submitGiveaway, { loading: submitting }] =
    useMutation(SUBMIT_USER_GIVEAWAY);

  const allPicked = headers.every((_, i) => !!files[i]);

  const handleOpenPicker = (idx: number) => inputRefs.current[idx]?.click();
  const handleBack = () => history.goBack();

  const cleanFiles = files.filter((f): f is File => f instanceof File);

  const handleSubmit = async () => {
    if (!giveaway?.id) return;
    if (!allPicked) {
      alert("Bitte alle erforderlichen Fotos hochladen.");
      return;
    }
    try {
      await submitGiveaway({
        variables: {
          giveawayId: giveaway.id,
          images: cleanFiles,
        },
      });
      alert("Erfolgreich teilgenommen!");
      history.goBack();
    } catch (err) {
      console.error(err);
      alert("Fehler beim Teilnehmen. Bitte erneut versuchen.");
    }
  };

  if (loading)
    return (
      <div className="giveaway-detail scrollable" style={{ padding: 12 }}>
        Laden…
      </div>
    );
  if (error)
    return (
      <div className="giveaway-detail scrollable" style={{ padding: 12 }}>
        Fehler beim Laden.
      </div>
    );
  if (!giveaway) return null;

  return (
    <div className="giveaway-detail scrollable" style={{ padding: 12, marginBottom: 80 }}>
      <div className="points-header">
        <img
          src={backArrow}
          alt="Zurück"
          className="points-header-arrow"
          onClick={handleBack}
          role="button"
          tabIndex={0}
        />
        <div className="points-header-title">Gewinnspiel</div>
        <div className="points-header-empty" />
      </div>

      {giveaway.imageUrl ? (
        <img
          src={giveaway.imageUrl}
          alt={giveaway.name}
          style={{ width: "100%", borderRadius: 14, marginTop: 16 }}
        />
      ) : null}

      <div className="giveaway-top" style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 16 }}>
        <h3 className="giveaway-title" style={{ margin: 0 }}>{giveaway.name}</h3>
        <img
          src={info}
          alt="Teilnahmebedingungen"
          role="button"
          tabIndex={0}
          onClick={() => setShowRules(true)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setShowRules(true)}
          style={{ cursor: "pointer" }}
        />
      </div>


{giveaway.tillDate && (
  <div
    className="giveaway-valid-until"
    style={{ marginTop: 8, color: "#7a7a7a", fontSize: 12, fontWeight: 500 }}
  >
    Gültig bis {new Date(giveaway.tillDate).toLocaleDateString("de-DE")}
  </div>
)}

      {giveaway.description && (
        <p className="giveaway-description" style={{ marginTop: 8 }}>
          {giveaway.description}
        </p>
      )}

      {isOpenForUser && (
        <>
          <div className="giveaway-pickers" style={{ marginTop: 16, display: "grid", gap: 12 }}>
            {headers.map((label, idx) => (
              <div
                key={`${label}-${idx}`}
                className="giveaway-picker-row"
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  padding: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: "70%" }}>
                  <div style={{ fontWeight: 600 }}>{label}</div>
                  <div style={{ fontSize: 13, color: "#7a7a7a" }}>
                    {files[idx] ? "Foto hochgeladen ✔️" : "Noch kein Foto ausgewählt"}
                  </div>
                </div>

                <div>
                  <input
                    type="file"
                    ref={setInputRef(idx)}
                    hidden
                    accept="image/*"
                    onChange={onPick(idx)}
                  />
                  <button
                    type="button"
                    onClick={() => handleOpenPicker(idx)}
                    style={{
                      borderRadius: 12,
                      padding: "10px 16px",
                      border: "1px solid #d6dde3",
                      background: "#fff",
                      fontWeight: 600,
                    }}
                  >
                    {files[idx] ? "Erneut wählen" : "Foto auswählen"}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            disabled={!allPicked || submitting}
            onClick={handleSubmit}
            style={{
              marginTop: 20,
              width: "100%",
              height: 44,
              borderRadius: 12,
              border: "none",
              background: allPicked ? "#94D3F2" : "#cfeaf8",
              color: "#fff",
              fontWeight: 500,
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? "Wird gesendet…" : "Jetzt teilnehmen"}
          </button>
        </>
      )}

      {showRules && (
        <div
          className="modal-overlay"
          onClick={() => setShowRules(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          aria-modal="true"
          role="dialog"
        >
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxHeight: "80vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              padding: 20,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 18 }}>Teilnahmebedingungen</div>
              <button
                onClick={() => setShowRules(false)}
                aria-label="Schließen"
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 20,
                  cursor: "pointer",
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                <img src={closeIcon} alt="Close" style={{ width: '20px'}}/>
              </button>
            </div>

            <div style={{ marginTop: 12, color: "#4d4d4d", whiteSpace: "pre-wrap" }}>
              {giveaway.participantRules || "Keine Teilnahmebedingungen verfügbar."}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiveawayDetail;
